import { ISortItem } from '@components/product/Filters/CollectionMobileFilter'
import { NextRouter } from 'next/router'
import { ParsedUrlQuery } from 'querystring'

interface DataItem {
  value: string
  count: number
  type: string
  data?: any
}

interface Data {
  [key: string]: DataItem[]
}

interface FilterData {
  name: string
  count: number
}

export interface FilterObject {
  [key: string]: string[]
}

export interface FilterItem {
  Key: string
  Value: string
  IsSelected: boolean
}

interface Filter {
  name: string
  key?: string
  displayOrder?: number
  items: FilterData[]
}

export const transformData = (data: Data): Filter[] => {
  const tempData: Filter[] = []
  const filters: Filter[] = []

  for (const [key, value] of Object.entries(data)) {
    if (value?.length > 0 && value[0]?.type === 'value') {
      const name = key[0].toUpperCase() + key.slice(1)
      tempData.push({ name, items: [] })

      for (const item of value[0]?.data) {
        tempData[tempData.length - 1].items.push({
          name: item.value,
          count: item.count,
        })
      }
    }
  }

  tempData.forEach(({ name, items }) => {
    // filter size duplicate values and smallCase value
    if (name == 'Size') {
      let filtersValue = items.filter(
        (obj) =>
          !items.some(
            (o) => o.name !== obj.name && o.name.toLowerCase() === obj.name
          )
      )
      items = filtersValue
    }
    filters.push({
      name,
      key: name,
      displayOrder: 0,
      items: items.map((item, index) => ({
        ...item,
        displayOrder: index,
      })),
    })
  })

  return filters
}

export const dataMapper = (response: any) => {
  let productValueMap: Record<string, string> = {
    variantGroupCode: 'variantGroupCode',
    title: 'name',
    stockcode: 'stockCode',
    imageurl: 'image',
    sku: 'sku',
    weburl: 'slug',
    brand: 'brand',
    id: 'productId',
    categories: 'categories',
  }
  return response.map((product: any) => {
    const transformedProduct: any = { attributes: [] }
    for (const key in product) {
      const mappedKey = productValueMap[key]
      if (product[key]?.raw !== undefined && mappedKey) {
        transformedProduct[mappedKey] = product[key].raw
      } else if (key == 'averagerating') {
        transformedProduct['rating'] = Number(product[key].raw).toFixed(1)
      } else if (key == 'color') {
        transformedProduct['attributes'] = [
          { key: 'product.colours', value: product[key]?.raw },
        ]
      } else if (key == 'price') {
        transformedProduct['price'] = {
          minPrice: product['price']?.raw,
          maxPrice: product['rrp']?.raw,
        }
      } else if (key == 'merchandisingconfig') {
        transformedProduct['attributes'] = [
          { key: 'product.merchandisingconfig', value: product[key]?.raw?.[0] },
          ...transformedProduct['attributes'],
        ]
      } else if (key == 'plpmsg1') {
        transformedProduct['attributes'] = [
          { key: 'product.plpmsg1', value: product[key]?.raw },
          ...transformedProduct['attributes'],
        ]
      }
    }
    return transformedProduct
  })
}

export const parseUrlToObjects = (
  url: string
): { Key: string; Value: string; IsSelected: boolean }[] => {
  const searchParams = new URLSearchParams(url)
  const result: { Key: string; Value: string; IsSelected: boolean }[] = []

  for (const [key, value] of searchParams.entries()) {
    if (key?.includes('filter')) {
      const values = value.split(':')
      values.forEach((val) => {
        if (val?.includes('#search')) {
          val = val?.split('#search')[0]
        }
        result.push({
          Key: key?.split('.')[0],
          Value: val,
          IsSelected: true,
        })
      })
    }
  }

  return result
}
const findFilterByKey = (key: string, filters: any) => {
  return filters?.find((productFilter: any) => {
    return (
      productFilter['name'] &&
      productFilter['name']?.toLowerCase()?.includes(key?.toLowerCase())
    )
  })
}

export const parseUrlToFilterForPlp = (url: string, filters: any) => {
  const searchParams = new URLSearchParams(url)
  const result: {
    Key: string
    Value: string
    name: string
    IsSelected: boolean
  }[] = []
  for (const [key, value] of searchParams.entries()) {
    if (key?.includes('filter')) {
      const values = value.split(':').map((val) => val.split('#')[0]) // Ensure # values are removed
      const filerObj = findFilterByKey(key?.split('.')[0], filters)
      values.forEach((val) => {
        if (val.trim()) {
          result.push({
            Key: filerObj?.key,
            name: filerObj?.name,
            Value: val,
            IsSelected: true,
          })
        }
      })
    }
  }

  return result
}

export const getSortValueForPlp = (url: string): string => {
  const queryParam = new URLSearchParams(url)
  const queryValueMapper: Record<string, string> = {
    price_asc: '1',
    price_desc: '2',
    discount: '6',
  }
  for (const [key, value] of queryParam.entries()) {
    if (key?.includes('sort')) {
      return queryValueMapper[`${value}`]
    }
  }

  return ''
}
export const getSortValue = (queryParam: ParsedUrlQuery): string => {
  const queryValueMapper: Record<string, string> = {
    price_asc: '1',
    price_desc: '2',
    discount: '6',
  }
  if (queryParam['sort']) {
    return queryValueMapper[`${queryParam['sort']}`]
  }
  return ''
}

export const updateFilterToQueryParams = (
  keyToUpdate: string,
  value: string,
  router: NextRouter
) => {
  const queryParams: any = { ...router.query }

  if (queryParams[keyToUpdate]) {
    queryParams[keyToUpdate] += `:${value}`
  } else {
    queryParams[keyToUpdate] = value
  }

  const updatedQueryString = new URLSearchParams(queryParams).toString()
  const newUrl = `${router.pathname}?${updatedQueryString}`
  router.replace(newUrl, undefined, { shallow: true })
}

export const removeValueFromQueryParam = (
  keyToUpdate: string,
  valueToRemove: string,
  router: NextRouter
) => {
  const queryParams: any = { ...router.query }

  if (queryParams[keyToUpdate]) {
    const valuesArray = queryParams[keyToUpdate].split(':')
    const updatedValuesArray = valuesArray.filter(
      (value: string) => value !== valueToRemove
    )
    if (updatedValuesArray.length === 0) {
      delete queryParams[keyToUpdate]
    } else {
      queryParams[keyToUpdate] = updatedValuesArray.join(':')
    }
  }

  const updatedQueryString = new URLSearchParams(queryParams).toString()
  const newUrl = `${router.pathname}?${updatedQueryString}`
  router.replace(newUrl, undefined, { shallow: true })
}

export const applyMultipleFilterToQueryParams = (
  filters: any[],
  router: NextRouter,
  sortItem?: ISortItem
) => {
  const queryParams: any = { ...router.query }
  if (filters?.length > 0) {
    for (const param in queryParams) {
      if (param.endsWith('.filter')) {
        delete queryParams[param]
      }
    }
    filters?.forEach((filter) => {
      const keyToUpdate = `${filter?.name}.filter`
      // if (queryParams[keyToUpdate]?.includes(filter?.Value)) {
      //   // return true
      //   delete queryParams[keyToUpdate]
      // }
      if (queryParams[keyToUpdate]) {
        queryParams[keyToUpdate] += `:${filter?.Value}`
      } else {
        queryParams[keyToUpdate] = filter?.Value
      }
    })
  } else {
    for (const param in queryParams) {
      if (param.endsWith('.filter')) {
        delete queryParams[param]
      }
    }
  }

  if (sortItem) {
    const sortMapper: Record<string, string> = {
      1: 'price_asc',
      2: 'price_desc',
      6: 'discount',
    }
    if (sortItem && sortMapper[sortItem?.key]) {
      queryParams['sort'] = sortMapper[sortItem?.key]
    } else if (sortItem?.key == '' && queryParams['sort']) {
      delete queryParams['sort']
    }
  }

  const updatedQueryString = new URLSearchParams(queryParams).toString()
  const newUrl = `${router.pathname}?${updatedQueryString}`
  router.replace(newUrl, undefined, { shallow: true })
}

export const clearFilterFromQuery = (router: NextRouter) => {
  const queryParams: any = { ...router.query }

  for (const param in queryParams) {
    if (param.endsWith('.filter') || param.endsWith('sort')) {
      delete queryParams[param]
    }
  }

  const updatedQueryString = new URLSearchParams(queryParams).toString()
  const newUrl = `${router.pathname}?${updatedQueryString}`

  router.replace(newUrl, undefined, { shallow: true })
}

export const sortWithQueryParam = (payload: any, router: NextRouter) => {
  const sortMapper: Record<string, string> = {
    1: 'price_asc',
    2: 'price_desc',
    6: 'discount',
  }
  const queryParams: any = { ...router.query }
  if (payload && payload in sortMapper) {
    queryParams['sort'] = sortMapper[payload]
  } else if (payload == '' && queryParams['sort']) {
    delete queryParams['sort']
  }
  const updatedQueryString = new URLSearchParams(queryParams).toString()
  const newUrl = `${router.pathname}?${updatedQueryString}`
  router.replace(newUrl, undefined, { shallow: true })
}
